(() => {
  if (customElements.get('hafizah-size-guide')) return;

  // Only enhance recognized, complete measurement rows. The original product
  // HTML remains visible when a future description has a different structure.
  function parseMeasurements(source) {
    const headings = {
      'waist': 'waist', 'محيط الخصر': 'waist', 'pinggang': 'waist',
      'garment bust': 'bust', 'shoulder': 'shoulder', 'sleeve': 'sleeve', 'length': 'length',
      'محيط صدر القطعة': 'bust', 'الكتف': 'shoulder', 'طول الكم': 'sleeve', 'الطول': 'length',
      'dada pakaian': 'bust', 'bahu': 'shoulder', 'lengan': 'sleeve', 'panjang': 'length'
    };
    const items = [...source.querySelectorAll('li')];
    if (!items.length) return null;
    const rows = [];
    for (const item of items) {
      const text = item.textContent.replace(/[\u2066-\u2069]/g, '').trim();
      const separator = text.indexOf(':');
      if (separator < 1) return null;
      const row = {size: text.slice(0, separator).trim(), values: {}};
      for (const part of text.slice(separator + 1).split(/[,،]/)) {
        const match = part.trim().match(/^(.+?)\s+(\d+(?:\.\d+)?)\s*(?:cm|سم)$/i);
        const key = match && headings[match[1].trim().toLowerCase()];
        if (!key || key in row.values || Number(match[2]) <= 0) return null;
        row.values[key] = {cm: Number(match[2]), original: match[2]};
      }
      if (!('bust' in row.values) || !('length' in row.values)) return null;
      rows.push(row);
    }
    const columns = ['bust', 'shoulder', 'waist', 'sleeve', 'length'].filter(key => rows.some(row => key in row.values));
    return {rows, columns};
  }

  class HafizahSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) {
        this.hideDuplicateMeasurements();
        return;
      }
      const source = this.querySelector('[data-size-source]');
      if (!source) return;
      const parsed = parseMeasurements(source);
      if (!parsed) return;
      const table = document.createElement('table');
      const caption = table.createCaption();
      caption.textContent = this.dataset.caption;
      const header = table.createTHead().insertRow();
      const addHeading = text => {
        const th = document.createElement('th');
        th.scope = 'col'; th.textContent = text; header.append(th);
      };
      addHeading(this.dataset.size);
      parsed.columns.forEach(key => addHeading(this.dataset[key]));
      const body = table.createTBody();
      for (const row of parsed.rows) {
        const tr = body.insertRow();
        const th = document.createElement('th');
        th.scope = 'row'; th.textContent = row.size; th.dir = 'auto'; tr.append(th);
        for (const key of parsed.columns) {
          const td = tr.insertCell();
          td.dir = 'ltr';
          if (row.values[key]) {
            td.dataset.cm = row.values[key].cm;
            td.dataset.original = row.values[key].original;
            td.textContent = row.values[key].original;
          } else {
            td.textContent = '—';
            td.setAttribute('aria-label', this.dataset.notListed);
          }
        }
      }
      this.querySelector('[data-size-table]').append(table);
      this.querySelector('[data-size-enhanced]').hidden = false;
      source.hidden = true;
      this.dataset.ready = 'true';
      this.querySelectorAll('[data-size-unit]').forEach(button => {
        button.addEventListener('click', () => {
          const inches = button.dataset.sizeUnit === 'in';
          this.querySelectorAll('[data-size-unit]').forEach(control => control.setAttribute('aria-pressed', String(control === button)));
          table.querySelectorAll('[data-cm]').forEach(cell => {
            cell.textContent = inches ? String(Math.round(Number(cell.dataset.cm) / 2.54 * 100) / 100) : cell.dataset.original;
          });
          caption.textContent = this.dataset.caption + ' (' + button.textContent.trim() + ')';
          this.querySelector('[data-inch-note]').hidden = !inches;
        });
      });
      caption.textContent += ' (' + this.querySelector('[data-size-unit="cm"]').textContent.trim() + ')';
      this.hideDuplicateMeasurements();
    }

    hideDuplicateMeasurements() {
      // Wait until following description markup has been parsed on initial load.
      cancelAnimationFrame(this.descriptionFrame);
      this.descriptionFrame = requestAnimationFrame(() => {
        if (!this.isConnected || this.dataset.ready !== 'true') return;
        const scope = this.closest('product-info');
        if (!scope) return;
        const normalize = node => node.textContent.replace(/\s+/g, ' ').trim();
        const sourceLists = [...this.querySelectorAll('[data-size-source] ul')].map(normalize);
        if (!sourceLists.length) return;
        this.hiddenMeasurements ||= new Map();
        for (const description of scope.querySelectorAll('.product__description')) {
          if (description.closest('product-info') !== scope) continue;
          for (const heading of description.querySelectorAll('h3')) {
            if (normalize(heading) !== this.dataset.caption) continue;
            const lists = [];
            for (let node = heading.nextElementSibling; node && !node.matches('h2, h3'); node = node.nextElementSibling) {
              if (node.matches('ul')) lists.push(node);
            }
            if (lists.length !== sourceLists.length || !lists.every((node, i) => normalize(node) === sourceLists[i])) continue;
            // Keep fit, model and measurement-tolerance paragraphs in place.
            for (const node of [heading, ...lists]) {
              if (this.hiddenMeasurements.has(node)) continue;
              this.hiddenMeasurements.set(node, {hidden: node.hidden, style: node.getAttribute('style')});
              node.hidden = true;
              node.style.setProperty('display', 'none', 'important');
            }
          }
        }
      });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.descriptionFrame);
      for (const [node, previous] of this.hiddenMeasurements || []) {
        node.hidden = previous.hidden;
        if (previous.style === null) node.removeAttribute('style');
        else node.setAttribute('style', previous.style);
      }
      this.hiddenMeasurements?.clear();
    }

  }
  customElements.define('hafizah-size-guide', HafizahSizeGuide);
  document.addEventListener('click', event => {
    const link = event.target.closest('a[data-hz-size-link]');
    if (!link) return;
    const details = document.getElementById(link.hash.slice(1));
    if (!details || details.tagName !== 'DETAILS') return;
    event.preventDefault();
    details.open = true;
    details.querySelector('summary').focus({preventScroll: true});
    details.scrollIntoView({block: 'nearest', behavior: 'auto'});
  });
})();
